import { ref, computed } from "vue";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import { API_BASE_URL } from "../../../consts";
import { useConfirmAction } from "./useConfirmAction";

const DEFAULT_FADE_OUT_SEC = 0.3;
const DEFAULT_DURATION_SEC = 60;
const MIN_SNIPPET_DURATION_SEC = 60;
const MAX_SNIPPET_DURATION_SEC = 180;
const MAX_SNIPPETS_PER_EPISODE = 5;

const clampCount = (value, max) =>
  Math.min(Math.max(Math.round(Number(value) || 1), 1), Math.max(max, 1));

const remainingCapacity = (existingCount) =>
  Math.max(0, MAX_SNIPPETS_PER_EPISODE - (existingCount || 0));

const clampDuration = (value) =>
  Math.min(
    Math.max(
      Math.round(Number(value) || DEFAULT_DURATION_SEC),
      MIN_SNIPPET_DURATION_SEC
    ),
    MAX_SNIPPET_DURATION_SEC
  );

const parseShowConfig = (show) => ({
  ...show,
  snippets_generation_config: show.snippets_generation_config
    ? JSON.parse(show.snippets_generation_config)
    : null,
  video_snippets_generation_config: show.video_snippets_generation_config
    ? JSON.parse(show.video_snippets_generation_config)
    : null,
});

export function useSnippetShows(token) {
  const toast = useToast();
  const { confirmAction } = useConfirmAction();

  const shows = ref([]);
  const showsLoading = ref(false);
  const generatingShowIds = ref(new Set());
  const showsSearchQuery = ref("");
  let showsSearchDebounceTimer = null;

  const fetchGeneratingShowIds = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/snippets/shows/generating-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      generatingShowIds.value = new Set(response.data.data.showIds);
    } catch (error) {
      console.error("Error fetching generating show ids:", error);
    }
  };

  const fetchShows = async () => {
    try {
      showsLoading.value = true;
      const response = await axios.get(`${API_BASE_URL}/podcast-shows`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          snippetsOnly: true,
          activeSellerCount: true,
          podcastGeneratedVideosCount: true,
          episodeCount: true,
          podcastVideosCount: true,
          search: showsSearchQuery.value || undefined,
        },
      });
      shows.value = (response.data || []).map(parseShowConfig);
    } catch (error) {
      console.error("Error fetching snippet shows:", error);
    } finally {
      showsLoading.value = false;
    }
    fetchGeneratingShowIds();
  };

  const onShowsSearchInput = () => {
    clearTimeout(showsSearchDebounceTimer);
    showsSearchDebounceTimer = setTimeout(() => {
      fetchShows();
    }, 400);
  };

  const removeShow = async (show) => {
    try {
      await axios.delete(`${API_BASE_URL}/snippets/shows/${show.show_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      shows.value = shows.value.filter((s) => s.show_id !== show.show_id);
      toast.add({
        severity: "success",
        summary: "Removed",
        detail: "Show removed from the snippets catalog.",
        life: 4000,
      });
    } catch (error) {
      console.error("Error removing snippet show:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to remove show.",
        life: 4000,
      });
    }
  };

  const confirmRemoveShow = (show) => {
    confirmAction({
      message: `Remove "${show.show_title}" from the snippets catalog?`,
      header: "Remove Confirmation",
      danger: true,
      accept: () => removeShow(show),
    });
  };

  const showEpisodeSelectDialog = ref(false);
  const episodeSelectShow = ref(null);
  const episodeSelectType = ref(null);
  const episodeSelectLoading = ref(false);
  const episodesForSelect = ref([]);
  const selectedEpisodeIds = ref([]);
  const episodeSettings = ref({});
  const generatingSnippets = ref(false);

  const snippetsForSelect = ref([]);
  const selectedSnippetIds = ref([]);
  const generatingVideos = ref(false);

  const videoSourceType = ref("static");
  const podcastVideosForSelect = ref([]);
  const selectedPodcastVideoIds = ref([]);
  const podcastVideoSettings = ref({});
  const dynamicVideoCaptionsDisabled = ref(false);

  const defaultAudioDuration = computed(
    () =>
      episodeSelectShow.value?.snippets_generation_config?.duration ??
      DEFAULT_DURATION_SEC
  );

  const defaultVideoDuration = computed(
    () =>
      episodeSelectShow.value?.video_snippets_generation_config?.dynamic
        ?.duration ?? DEFAULT_DURATION_SEC
  );

  const episodeRemainingCapacity = (episodeId) => {
    const episode = episodesForSelect.value.find(
      (e) => e.episode_id === episodeId
    );
    return remainingCapacity(episode?.snippet_count);
  };

  const podcastVideoRemainingCapacity = (videoId) => {
    const video = podcastVideosForSelect.value.find(
      (v) => v.video_id === videoId
    );
    return remainingCapacity(video?.dynamic_snippet_count);
  };

  const setEpisodeCount = (episodeId, count) => {
    if (!episodeSettings.value[episodeId]) return;
    episodeSettings.value[episodeId].count = clampCount(
      count,
      episodeRemainingCapacity(episodeId)
    );
  };

  const setEpisodeDuration = (episodeId, duration) => {
    if (!episodeSettings.value[episodeId]) return;
    episodeSettings.value[episodeId].duration = clampDuration(duration);
  };

  const setPodcastVideoCount = (videoId, count) => {
    if (!podcastVideoSettings.value[videoId]) return;
    podcastVideoSettings.value[videoId].count = clampCount(
      count,
      podcastVideoRemainingCapacity(videoId)
    );
  };

  const setPodcastVideoDuration = (videoId, duration) => {
    if (!podcastVideoSettings.value[videoId]) return;
    podcastVideoSettings.value[videoId].duration = clampDuration(duration);
  };

  const generateSnippetsConfirmItems = computed(() =>
    selectedEpisodeIds.value.map((episodeId) => {
      const episode = episodesForSelect.value.find(
        (e) => e.episode_id === episodeId
      );
      const settings = episodeSettings.value[episodeId] || {
        count: 1,
        duration: defaultAudioDuration.value,
      };
      return `${episode?.episode_title || episodeId} — ${
        settings.count
      } snippet${settings.count === 1 ? "" : "s"}, ${settings.duration}s each`;
    })
  );

  const generateSnippetsConfirmMessage = computed(() => {
    const episodeCount = selectedEpisodeIds.value.length;
    const totalSnippets = selectedEpisodeIds.value.reduce(
      (sum, id) => sum + (episodeSettings.value[id]?.count ?? 1),
      0
    );
    return `Are you sure you want to generate ${totalSnippets} snippet${
      totalSnippets === 1 ? "" : "s"
    } from ${episodeCount} episode${episodeCount === 1 ? "" : "s"}?`;
  });

  const generateVideosConfirmItems = computed(() =>
    selectedSnippetIds.value.map((snippetId) => {
      const snippet = snippetsForSelect.value.find(
        (s) => s.snippet_id === snippetId
      );
      return snippet?.episode_title || snippetId;
    })
  );

  const generateVideosConfirmMessage = computed(() => {
    const count = selectedSnippetIds.value.length;
    return `Are you sure you want to generate ${count} static video${
      count === 1 ? "" : "s"
    } based on ${count === 1 ? "this snippet" : "these snippets"}?`;
  });

  const generateDynamicVideosConfirmItems = computed(() =>
    selectedPodcastVideoIds.value.map((videoId) => {
      const video = podcastVideosForSelect.value.find(
        (v) => v.video_id === videoId
      );
      const settings = podcastVideoSettings.value[videoId] || {
        count: 1,
        duration: defaultVideoDuration.value,
      };
      return `${video?.title || "Untitled video"} — ${settings.count} snippet${
        settings.count === 1 ? "" : "s"
      }, ${settings.duration}s each`;
    })
  );

  const generateDynamicVideosConfirmMessage = computed(() => {
    const count = selectedPodcastVideoIds.value.length;
    const totalClips = selectedPodcastVideoIds.value.reduce(
      (sum, id) => sum + (podcastVideoSettings.value[id]?.count ?? 1),
      0
    );
    return `Are you sure you want to generate ${totalClips} video snippet${
      totalClips === 1 ? "" : "s"
    } from ${count} imported video${count === 1 ? "" : "s"}?`;
  });

  const openEpisodeSelectDialog = (show) => {
    episodeSelectShow.value = show;
    episodeSelectType.value = null;
    selectedEpisodeIds.value = [];
    episodesForSelect.value = [];
    episodeSettings.value = {};
    videoSourceType.value = "static";
    selectedSnippetIds.value = [];
    snippetsForSelect.value = [];
    selectedPodcastVideoIds.value = [];
    podcastVideosForSelect.value = [];
    podcastVideoSettings.value = {};
    dynamicVideoCaptionsDisabled.value =
      show.video_snippets_generation_config?.dynamic?.captions_disabled ??
      false;
    showEpisodeSelectDialog.value = true;
  };

  const fetchVideoSourceList = async () => {
    if (!episodeSelectShow.value) return;

    episodeSelectLoading.value = true;
    try {
      if (videoSourceType.value === "static") {
        const response = await axios.get(
          `${API_BASE_URL}/snippets/shows/${episodeSelectShow.value.show_id}/video-snippets`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        snippetsForSelect.value = response.data.data.snippets;
      } else {
        const response = await axios.get(
          `${API_BASE_URL}/snippets/shows/${episodeSelectShow.value.show_id}/podcast-videos`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        podcastVideosForSelect.value = response.data.data.videos;
      }
    } catch (error) {
      console.error("Error fetching video source list:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to load list.",
        life: 4000,
      });
    } finally {
      episodeSelectLoading.value = false;
    }
  };

  const selectVideoSourceType = (sourceType) => {
    videoSourceType.value = sourceType;
    selectedSnippetIds.value = [];
    selectedPodcastVideoIds.value = [];
    fetchVideoSourceList();
  };

  const selectEpisodeType = async (type) => {
    episodeSelectType.value = type;
    if (!episodeSelectShow.value) return;

    if (type === "video") {
      videoSourceType.value = "static";
      await fetchVideoSourceList();
      return;
    }

    episodeSelectLoading.value = true;
    try {
      const response = await axios.get(
        `${API_BASE_URL}/snippets/shows/${episodeSelectShow.value.show_id}/episodes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      episodesForSelect.value = response.data.data.episodes;
    } catch (error) {
      console.error("Error fetching episodes for show:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to load list.",
        life: 4000,
      });
      episodeSelectType.value = null;
    } finally {
      episodeSelectLoading.value = false;
    }
  };

  const closeEpisodeSelectDialog = () => {
    showEpisodeSelectDialog.value = false;
  };

  const toggleEpisodeSelection = (episodeId) => {
    const index = selectedEpisodeIds.value.indexOf(episodeId);
    if (index === -1) {
      if (episodeRemainingCapacity(episodeId) === 0) return;
      selectedEpisodeIds.value.push(episodeId);
      episodeSettings.value[episodeId] = {
        count: 1,
        duration: defaultAudioDuration.value,
      };
    } else {
      selectedEpisodeIds.value.splice(index, 1);
      delete episodeSettings.value[episodeId];
    }
  };

  const toggleSnippetSelection = (snippetId) => {
    const index = selectedSnippetIds.value.indexOf(snippetId);
    if (index === -1) {
      const snippet = snippetsForSelect.value.find(
        (s) => s.snippet_id === snippetId
      );
      if (snippet?.has_video) return;
      selectedSnippetIds.value.push(snippetId);
    } else {
      selectedSnippetIds.value.splice(index, 1);
    }
  };

  const togglePodcastVideoSelection = (videoId) => {
    const index = selectedPodcastVideoIds.value.indexOf(videoId);
    if (index === -1) {
      if (podcastVideoRemainingCapacity(videoId) === 0) return;
      selectedPodcastVideoIds.value.push(videoId);
      podcastVideoSettings.value[videoId] = {
        count: 1,
        duration: defaultVideoDuration.value,
      };
    } else {
      selectedPodcastVideoIds.value.splice(index, 1);
      delete podcastVideoSettings.value[videoId];
    }
  };

  const confirmGenerateSnippetsForSelectedEpisodes = () => {
    if (!episodeSelectShow.value || !selectedEpisodeIds.value.length) return;

    confirmAction({
      message: generateSnippetsConfirmMessage.value,
      header: "Generate Confirmation",
      items: generateSnippetsConfirmItems.value,
      accept: () => generateSnippetsForSelectedEpisodes(),
    });
  };

  const generateSnippetsForSelectedEpisodes = async () => {
    if (!episodeSelectShow.value || !selectedEpisodeIds.value.length) return;

    generatingSnippets.value = true;
    try {
      const episodes = selectedEpisodeIds.value.map((episodeId) => ({
        episodeId,
        durationSec:
          episodeSettings.value[episodeId]?.duration ??
          defaultAudioDuration.value,
        snippetsPerEpisode: episodeSettings.value[episodeId]?.count ?? 1,
      }));
      await axios.post(
        `${API_BASE_URL}/snippets/shows/${episodeSelectShow.value.show_id}/generate-snippets`,
        { episodes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Queued",
        detail: `Snippet generation queued for ${
          selectedEpisodeIds.value.length
        } episode${selectedEpisodeIds.value.length === 1 ? "" : "s"}.`,
        life: 4000,
      });
      showEpisodeSelectDialog.value = false;
      fetchGeneratingShowIds();
    } catch (error) {
      console.error("Error generating snippets for episodes:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to generate snippets.",
        life: 4000,
      });
    } finally {
      generatingSnippets.value = false;
    }
  };

  const confirmGenerateVideosForSelectedSnippets = () => {
    if (!episodeSelectShow.value || !selectedSnippetIds.value.length) return;

    confirmAction({
      message: generateVideosConfirmMessage.value,
      header: "Generate Video Confirmation",
      items: generateVideosConfirmItems.value,
      accept: () => generateVideosForSelectedSnippets(),
    });
  };

  const generateVideosForSelectedSnippets = async () => {
    if (!episodeSelectShow.value || !selectedSnippetIds.value.length) return;

    generatingVideos.value = true;
    try {
      await axios.post(
        `${API_BASE_URL}/snippets/shows/${episodeSelectShow.value.show_id}/generate-static-videos`,
        { snippetIds: selectedSnippetIds.value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Queued",
        detail: `Video generation queued for ${
          selectedSnippetIds.value.length
        } snippet${selectedSnippetIds.value.length === 1 ? "" : "s"}.`,
        life: 4000,
      });
      showEpisodeSelectDialog.value = false;
    } catch (error) {
      console.error("Error generating videos for snippets:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to generate videos.",
        life: 4000,
      });
    } finally {
      generatingVideos.value = false;
    }
  };

  const confirmGenerateVideosForSelectedPodcastVideos = () => {
    if (!episodeSelectShow.value || !selectedPodcastVideoIds.value.length)
      return;

    confirmAction({
      message: generateDynamicVideosConfirmMessage.value,
      header: "Generate Video Confirmation",
      items: generateDynamicVideosConfirmItems.value,
      accept: () => generateVideosForSelectedPodcastVideos(),
    });
  };

  const generateVideosForSelectedPodcastVideos = async () => {
    if (!episodeSelectShow.value || !selectedPodcastVideoIds.value.length)
      return;

    generatingVideos.value = true;
    try {
      const videos = selectedPodcastVideoIds.value.map((podcastVideoId) => ({
        podcastVideoId,
        durationSec:
          podcastVideoSettings.value[podcastVideoId]?.duration ??
          defaultVideoDuration.value,
        snippetsPerEpisode:
          podcastVideoSettings.value[podcastVideoId]?.count ?? 1,
      }));
      await axios.post(
        `${API_BASE_URL}/snippets/shows/${episodeSelectShow.value.show_id}/generate-dynamic-videos`,
        { videos, captionsDisabled: dynamicVideoCaptionsDisabled.value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Queued",
        detail: `Video generation queued for ${
          selectedPodcastVideoIds.value.length
        } imported video${
          selectedPodcastVideoIds.value.length === 1 ? "" : "s"
        }.`,
        life: 4000,
      });
      showEpisodeSelectDialog.value = false;
    } catch (error) {
      console.error("Error generating videos for podcast videos:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to generate videos.",
        life: 4000,
      });
    } finally {
      generatingVideos.value = false;
    }
  };

  const showAddShowDialog = ref(false);
  const availableShowsToAdd = ref([]);
  const availableShowsLoading = ref(false);
  const showToAdd = ref(null);
  const addShowSubmitting = ref(false);

  const newShowDuration = ref(60);
  const newShowSnippetsPerEpisode = ref(1);
  const newShowFadeOut = ref(DEFAULT_FADE_OUT_SEC);
  const showAdvancedAudio = ref(false);
  const newShowVideoGenerationEnabled = ref(false);
  const audioSnippetsEnabled = ref(false);

  const videoSnippetsEnabled = ref(false);
  const newVideoSnippetLength = ref(60);
  const newVideoSnippetsPerEpisode = ref(1);
  const newVideoCaptionsDisabled = ref(false);

  const showAddShowConfirmStep = ref(false);
  const editingShow = ref(null);

  const resetAddShowForm = () => {
    showToAdd.value = null;
    editingShow.value = null;
    newShowDuration.value = 60;
    newShowSnippetsPerEpisode.value = 1;
    newShowFadeOut.value = DEFAULT_FADE_OUT_SEC;
    showAdvancedAudio.value = false;
    newShowVideoGenerationEnabled.value = false;
    audioSnippetsEnabled.value = false;
    videoSnippetsEnabled.value = false;
    newVideoSnippetLength.value = 60;
    newVideoSnippetsPerEpisode.value = 1;
    newVideoCaptionsDisabled.value = false;
    showAddShowConfirmStep.value = false;
  };

  const openAddShowDialog = async () => {
    resetAddShowForm();
    showAddShowDialog.value = true;
    availableShowsLoading.value = true;

    try {
      const response = await axios.get(`${API_BASE_URL}/podcast-shows`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { excludeSnippetsEnabled: true },
      });
      availableShowsToAdd.value = response.data || [];
    } catch (error) {
      console.error("Error fetching podcast shows:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to load shows.",
        life: 4000,
      });
    } finally {
      availableShowsLoading.value = false;
    }
  };

  const addShow = async () => {
    if (!showToAdd.value) return;

    try {
      addShowSubmitting.value = true;
      await axios.post(
        `${API_BASE_URL}/snippets/shows`,
        {
          showId: showToAdd.value.show_id,
          duration: newShowDuration.value,
          snippetsPerEpisode: newShowSnippetsPerEpisode.value,
          fadeOutSec: newShowFadeOut.value,
          videoGenerationEnabled: newShowVideoGenerationEnabled.value,
          audioEnabled: audioSnippetsEnabled.value,
          videoEnabled: videoSnippetsEnabled.value,
          videoDuration: newVideoSnippetLength.value,
          videoSnippetsPerEpisode: newVideoSnippetsPerEpisode.value,
          videoCaptionsDisabled: newVideoCaptionsDisabled.value,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Added",
        detail: "Show added to the snippets catalog.",
        life: 5000,
      });
      showAddShowDialog.value = false;
      await fetchShows();
    } catch (error) {
      console.error("Error adding snippet show:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to add show.",
        life: 4000,
      });
    } finally {
      addShowSubmitting.value = false;
    }
  };

  const openEditShowDialog = (show) => {
    resetAddShowForm();
    editingShow.value = show;
    newShowDuration.value = show.snippets_generation_config?.duration ?? 60;
    newShowSnippetsPerEpisode.value =
      show.snippets_generation_config?.snippets_per_episode ?? 1;
    newShowFadeOut.value = show.snippets_generation_config?.fade_out_sec ?? 0;
    showAdvancedAudio.value = newShowFadeOut.value > 0;
    newShowVideoGenerationEnabled.value =
      show.video_snippets_generation_config?.static
        ?.is_auto_generation_enabled ?? false;
    audioSnippetsEnabled.value =
      show.snippets_generation_config?.enabled ?? true;
    videoSnippetsEnabled.value =
      show.video_snippets_generation_config?.dynamic?.enabled ?? true;
    newVideoSnippetLength.value =
      show.video_snippets_generation_config?.dynamic?.duration ?? 60;
    newVideoSnippetsPerEpisode.value =
      show.video_snippets_generation_config?.dynamic?.snippets_per_episode ?? 1;
    newVideoCaptionsDisabled.value =
      show.video_snippets_generation_config?.dynamic?.captions_disabled ??
      false;
    showAddShowDialog.value = true;
  };

  const saveShowConfig = async () => {
    if (!editingShow.value) return;

    try {
      addShowSubmitting.value = true;
      await axios.put(
        `${API_BASE_URL}/snippets/shows/${editingShow.value.show_id}/config`,
        {
          duration: newShowDuration.value,
          snippetsPerEpisode: newShowSnippetsPerEpisode.value,
          fadeOutSec: newShowFadeOut.value,
          videoGenerationEnabled: newShowVideoGenerationEnabled.value,
          audioEnabled: audioSnippetsEnabled.value,
          videoEnabled: videoSnippetsEnabled.value,
          videoDuration: newVideoSnippetLength.value,
          videoSnippetsPerEpisode: newVideoSnippetsPerEpisode.value,
          videoCaptionsDisabled: newVideoCaptionsDisabled.value,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Saved",
        detail: "Snippet config updated.",
        life: 4000,
      });
      showAddShowDialog.value = false;
      await fetchShows();
    } catch (error) {
      console.error("Error updating snippet show config:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail:
          error.response?.data?.message || "Failed to update show config.",
        life: 4000,
      });
    } finally {
      addShowSubmitting.value = false;
    }
  };

  // Messages, not booleans: PrimeVue's :min only clamps on blur, so a typed 59
  // stays on screen and the admin needs to be told why Save is dead.
  const durationError = computed(() => {
    const value = newShowDuration.value;
    if (value == null || value < 60) {
      return "Minimum snippet length is 60 seconds.";
    }
    if (value > 180) {
      return "Maximum snippet length is 180 seconds.";
    }
    return null;
  });

  const snippetsPerEpisodeError = computed(() => {
    const value = newShowSnippetsPerEpisode.value;
    if (value == null || value < 1 || value > 5) {
      return "Choose between 1 and 5 snippets per episode.";
    }
    return null;
  });

  const fadeOutError = computed(() => {
    const value = newShowFadeOut.value;
    if (value == null || value < 0 || value > 5) {
      return "Fade-out must be between 0 and 5 seconds.";
    }
    return null;
  });

  const videoDurationError = computed(() => {
    const value = newVideoSnippetLength.value;
    if (value == null || value < 60) {
      return "Minimum snippet length is 60 seconds.";
    }
    if (value > 180) {
      return "Maximum snippet length is 180 seconds.";
    }
    return null;
  });

  const videoSnippetsPerEpisodeError = computed(() => {
    const value = newVideoSnippetsPerEpisode.value;
    if (value == null || value < 1 || value > 5) {
      return "Choose between 1 and 5 snippets per episode.";
    }
    return null;
  });

  const snippetConfigInvalid = computed(
    () =>
      (audioSnippetsEnabled.value &&
        (!!durationError.value ||
          !!snippetsPerEpisodeError.value ||
          !!fadeOutError.value)) ||
      (videoSnippetsEnabled.value &&
        (!!videoDurationError.value || !!videoSnippetsPerEpisodeError.value))
  );

  const addShowConfirmMessage = computed(() => {
    if (!showToAdd.value) return "";

    const parts = [];
    if (audioSnippetsEnabled.value) {
      parts.push(
        `Audio Snippets — ${newShowSnippetsPerEpisode.value} snippet(s) per episode, ${newShowDuration.value}s each`
      );
    }
    if (videoSnippetsEnabled.value) {
      parts.push(
        `Video Snippets — ${newVideoSnippetsPerEpisode.value} snippet(s) per episode, ${newVideoSnippetLength.value}s each`
      );
    }

    const automationSummary = parts.length
      ? parts.join("; ")
      : "no automations enabled — you can turn them on anytime";

    return `Add "${showToAdd.value.show_title}" to the snippets catalog: ${automationSummary}. Continue?`;
  });

  const goToAddShowConfirmStep = () => {
    if (!showToAdd.value) return;
    showAddShowConfirmStep.value = true;
  };

  const cancelAddShowConfirmStep = () => {
    showAddShowConfirmStep.value = false;
  };

  return {
    shows,
    showsLoading,
    generatingShowIds,
    showsSearchQuery,
    fetchShows,
    onShowsSearchInput,
    confirmRemoveShow,
    showEpisodeSelectDialog,
    episodeSelectShow,
    episodeSelectType,
    episodeSelectLoading,
    episodesForSelect,
    selectedEpisodeIds,
    episodeSettings,
    generatingSnippets,
    snippetsForSelect,
    selectedSnippetIds,
    generatingVideos,
    videoSourceType,
    podcastVideosForSelect,
    selectedPodcastVideoIds,
    podcastVideoSettings,
    dynamicVideoCaptionsDisabled,
    defaultAudioDuration,
    defaultVideoDuration,
    setEpisodeCount,
    setEpisodeDuration,
    setPodcastVideoCount,
    setPodcastVideoDuration,
    episodeRemainingCapacity,
    podcastVideoRemainingCapacity,
    openEpisodeSelectDialog,
    closeEpisodeSelectDialog,
    selectEpisodeType,
    selectVideoSourceType,
    toggleEpisodeSelection,
    toggleSnippetSelection,
    togglePodcastVideoSelection,
    confirmGenerateSnippetsForSelectedEpisodes,
    confirmGenerateVideosForSelectedSnippets,
    confirmGenerateVideosForSelectedPodcastVideos,
    showAddShowDialog,
    availableShowsToAdd,
    availableShowsLoading,
    showToAdd,
    addShowSubmitting,
    newShowDuration,
    newShowSnippetsPerEpisode,
    newShowFadeOut,
    showAdvancedAudio,
    newShowVideoGenerationEnabled,
    audioSnippetsEnabled,
    videoSnippetsEnabled,
    newVideoSnippetLength,
    newVideoSnippetsPerEpisode,
    newVideoCaptionsDisabled,
    showAddShowConfirmStep,
    addShowConfirmMessage,
    openAddShowDialog,
    addShow,
    goToAddShowConfirmStep,
    cancelAddShowConfirmStep,
    editingShow,
    openEditShowDialog,
    saveShowConfig,
    snippetConfigInvalid,
    durationError,
    snippetsPerEpisodeError,
    fadeOutError,
    videoDurationError,
    videoSnippetsPerEpisodeError,
  };
}
